/**
 *
 * @module storage
 *
 */

import Check from 'check-types';
import Logging from './../../../../logging/entry';
import CoreStorageProvider from './../../shared/coreStorageProvider';
import Exception from './../../../exception/entry';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ProviderException = Exception.ProviderException;

const {
    assigned
} = Check.assert;

/**
 *
 * @class Storage
 * @extends {CoreStorageProvider}
 * @desc Trilithium Storage provider implementation.
 *
 * @example
 * const storage = new Storage(logger);
 *
 */
export default class Storage extends CoreStorageProvider {

    /**
     *
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @throws {ProviderException}
     *
     */
    constructor(logger = Logging) {

        const errorMessage = {
            ctor: 'Storage(logger)',
            get localStorage() {
                return `${this.ctor} expected global.engine.storage.local instance`;
            }
        };

        const localStorageInstance = (function localStorageInstance() {
            return Check.assigned(global)
                && Check.assigned(global.engine)
                && Check.assigned(global.engine.storage)
                && Check.assigned(global.engine.storage.local);
        }());

        Check.assert(localStorageInstance, errorMessage.localStorage, ProviderException);

        super(logger);

        /**
         *
         * @access private
         * @type {Object}
         * @desc storage reference
         *
         */
        this.store = global.engine.storage.local;

        this.logger.log('Storage', 'Created Trilithium Provider.');

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {String} signature - the current active method signature
     * @param {String} key - Storage key reference
     * @param {*} value - value to store with key
     * @throws {InvalidArgumentException}
     * @throws {ProviderException}
     * @returns {Promise<*>}
     *
     */
    op(signature, key, value) {

        const errorMessage = {
            get operationError() {
                return `${signature} cannot complete successfully`;
            },
            get keyError() {
                return `${signature} key should be a String with length`;
            },
            get keyMissingWarning() {
                return `${signature} reference cache key "${key}" does not exist`;
            },
            get valueParseError() {
                return `${signature} unable to parse value`;
            },
            get storeError() {
                return `${signature} missing expected store reference`;
            }
        };

        const operation = signature.slice(signature.indexOf('.') + 1, signature.indexOf('('));
        const isClear = Check.equal(operation, 'clear');
        const isRemove = Check.equal(operation, 'remove');

        const store = this.store;

        assigned(store, errorMessage.storeError, ProviderException);

        return new Promise((resolve, reject) => {

            const { logger, storedKeys } = this;

            /**
             *
             * @desc verify key validity for all operations except clear
             *
             */
            if (Check.equal(isClear, false) && (Check.not.string(key) || Check.emptyString(key))) {
                return reject(new InvalidArgumentException(errorMessage.keyError));
            }

            /**
             *
             * @desc process given operation
             * @note all items set in Storage are expected to be stringified values
             * @note JSON operations are processed in a try...catch
             * @note if a given key is not found, fail silently and display warning
             *
             */
            try {

                if (isClear) {

                    // remove all known keys
                    storedKeys.map((keyInStorage) => {
                        return delete store[keyInStorage];
                    });

                    return resolve();

                }

                if (isRemove) {

                    // remove item and key from reference
                    storedKeys.splice(storedKeys.indexOf(key), 1);

                    delete store[key];

                    return resolve();

                } else if (Check.not.undefined(value)) {

                    // set item if valid value provided
                    // stringify before updating stored keys reference for accuracy
                    const val = JSON.stringify(value);

                    storedKeys.push(key);

                    store[key] = val;

                    return resolve();

                } else {

                    // if valid value not provided, attempt to get cached item
                    try {

                        const item = store[key];

                        if (Check.not.assigned(item)) {
                            logger.warn('Storage', errorMessage.keyMissingWarning);
                            return resolve();
                        }

                        return resolve(JSON.parse(item));

                    } catch (error) {

                        return reject(new InvalidArgumentException(`${errorMessage.valueParseError} - ${error}`));

                    }

                }

            } catch (error) {

                return reject(new ProviderException(`${errorMessage.operationError} - ${error}`));

            }

        });

    }

}



// WEBPACK FOOTER //
// ./src/services/platforms/sony/trilithium/storage.js